import React from 'react';

import ItemFieldRow from '../items/ItemFieldRow';
import RecordBadge from '../common/RecordBadge';
import MarkdownContent from '../common/MarkdownContent';
import BarcodeBlock from './BarcodeBlock';
import PageGroupBlock from './PageGroupBlock';
import PageRuler from './PageRuler';
import PageFormBackdrop from './PageFormBackdrop';
import { gridDrawStep } from './pageLayoutModel';
import { entriesOnLines, groupName, notShownWords, readableGroup } from './pageGroupLines';
import { blocksOnSheet, formSheets, hasForm, sheetPage } from './pageLayoutForm';
import { renderTemplateForItem } from '../../utils/displayTemplate';
import { elementLabel, formatValue } from '../items/itemValueDisplay';
import MaskedValue from '../common/MaskedValue';
import { isMaskedElement } from '../../utils/maskedElements';

/**
 * A card layout of kind 'page', laid out at REAL PHYSICAL SIZE.
 *
 * This is the whole reason the print half is client-side rather than an
 * extension of the server's pdfkit export: the editor's canvas and the printed
 * sheet are THIS component, at the same dimensions, reading the same stored
 * body. There is no second implementation of the layout to drift from the
 * first, and every value renderer the product already has — measurements with
 * their units, markdown, user chips, choice dots — works here unchanged.
 *
 * Coordinates are stored in the sheet's own unit (`in` or `mm`), so they go
 * straight into CSS as `8.5in` / `210mm` and the browser does the conversion
 * to device pixels and to printer dots. Nothing here is in pixels.
 *
 * `interactive` is the editor's flag: it draws the margin guides and lets a
 * caller decorate each block (selection, handles). Print never sets it.
 * Two more aids belong to it alone (KPR-345): `gridStep`, the grid's spacing in
 * the page's unit, draws the grid lines over the sheet, and `rulers` frames the
 * sheet with a ruler along its top and left edge. Neither reaches the printer.
 *
 * A FORM (KPR-1860): a layout with `body.form` draws one sheet per form page at
 * that page's own size, the form behind the blocks that belong to the sheet, and
 * `data-form-page` says which sheet is which. `formImages` is what
 * hooks/useFormPageImages.js answers. Until a sheet's page is in hand, a printed
 * sheet draws its waiting state and NO blocks: a record's values on a blank sheet
 * look like a filled form and are not one. The editor keeps its blocks, because
 * there they are the person's work; it hands the renderer a sample record on a
 * form (pageFormSample.js).
 *
 * On a form the BOX IS THE LIMIT (KPR-1861): print.css clips a block at its own
 * edges, and a block with something to fit carries `data-fit-name`, which is how
 * pageBlockFit.js finds what the box cuts and useBlockFit tells the person. A
 * plain layout draws exactly what it always did.
 *
 * AUTO SCALE (KPR-1862): an element or text block of a form with `style.autoScale`
 * and a value to fit also carries `data-autoscale`, and print.css draws everything in
 * it at `--fit-scale` of its size (`zoom`), a number pageBlockFit.measureBlocks writes
 * on the block after measuring the drawn sheet (1 until it has). The renderer never
 * decides the size: it only makes the block scalable, so the screen and the paper are
 * one DOM.
 *
 * A REPEATING GROUP ON RULED LINES (KPR-1863): a `group` block draws a cell for each
 * entry and chosen column (PageGroupBlock), and a cell is a box by these same rules.
 * The block itself carries what did not print, `data-not-shown`, for the same notice.
 */

/** A length in the sheet's own unit, for CSS. */
export const unit = (value, u) => `${Number(value) || 0}${u}`;

/** The style that places a block on the sheet. */
export function blockPosition(block, page) {
	return {
		position: 'absolute',
		left: unit(block.x, page.unit),
		top: unit(block.y, page.unit),
		width: unit(block.w, page.unit),
		...(block.type === 'rule' ? {} : { height: unit(block.h, page.unit) }),
	};
}

/** The type-agnostic text styling an author can set on any block. */
function blockTextStyle(style = {}) {
	return {
		...(style.size ? { fontSize: `${style.size}pt` } : {}),
		...(style.weight ? { fontWeight: style.weight } : {}),
		...(style.align ? { textAlign: style.align } : {}),
		...(style.italic ? { fontStyle: 'italic' } : {}),
	};
}

function ElementBlock({ block, element, item, resolveLookupValue, unitSystem }) {
	if (!element) return null;
	const value = item?.elements?.[element.name];
	// `label: false` prints the value alone — what a label or a form field
	// wants, where the paper already says what the number is. It reads
	// through the same renderer the labelled branch reaches via ItemFieldRow,
	// resolver and all: a value-only block printing "cdl-a" beside an
	// identical labelled block printing "CDL A" is one sheet contradicting
	// itself. The two branches agree on a MISS as well — ItemFieldRow's
	// card-lookup row goes through formatValue too, so an unresolved
	// reference is blank here and an em-dash there, never an id on either.
	if (block.style?.label === false) {
		return (
			<div className="page-block__value" style={blockTextStyle(block.style)}>
				{/* A masked element (KPR-1348): its mask with Show and Copy, as the labelled row has. */}
				{isMaskedElement(element)
					? <MaskedValue element={element} value={value} prefs={{ unitSystem }} resetKey={item?._id} empty="" />
					: (formatValue(element, value, { unitSystem, resolveLookupValue }) || '')}
			</div>
		);
	}
	return (
		<ItemFieldRow
			element={element}
			value={value}
			resolveLookupValue={resolveLookupValue}
			unitSystem={unitSystem}
			item={item}
		/>
	);
}

// A text block is a display template, so `{{element}}` tokens and their
// formatters work exactly as they do in a card's title — one grammar for
// "text with values in it", wherever it is written. No fallback: a block
// whose tokens resolve to nothing prints nothing, where a TITLE must
// always find something to call the record by.
const textOf = (block, item, elementDefs, resolveLookupValue) => renderTemplateForItem({
	template: block.template || '',
	elements: item?.elements || {},
	elementDefinitions: elementDefs,
	resolveLookupValue,
});

/**
 * What a block on a FORM is called when its value does not fit, or null when it
 * has nothing to fit (KPR-1861): an empty value, an empty text, a rule, a barcode
 * (which refuses to be drawn too big, BarcodeBlock). Written as `data-fit-name`,
 * which is what pageBlockFit.measureBlocks reads; a plain layout never gets one.
 */
function fitName(block, record, byName, elementDefs, resolveLookupValue) {
	if (block.type === 'element') {
		const element = byName.get(block.element);
		const value = record?.elements?.[block.element];
		const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
		return element && !empty ? elementLabel(element) || block.element : null;
	}
	if (block.type === 'text') {
		const text = textOf(block, record, elementDefs, resolveLookupValue).replace(/\s+/g, ' ').trim();
		if (!text) return null;
		return `Text block "${text.length > 24 ? `${text.slice(0, 24)}…` : text}"`;
	}
	if (block.type === 'badge') return 'Record badge';
	if (block.type === 'query') return 'Related records';
	return null;
}

function TextBlock({ block, item, elementDefs, resolveLookupValue }) {
	const text = textOf(block, item, elementDefs, resolveLookupValue);
	return (
		<div className="page-block__text" style={blockTextStyle(block.style)}>
			<MarkdownContent markdown={text} />
		</div>
	);
}

export default function PageLayoutRender({
	layout,
	card,
	elementDefs = [],
	item = null,
	items = [],
	resolveLookupValue,
	unitSystem = 'default',
	interactive = false,
	gridStep = null,
	rulers = false,
	guides = null,
	formImages = null,
	onRetryForm,
	renderBlock,
	renderQueryBlock,
	className = '',
}) {
	const body = layout?.body;
	const page = body?.page;
	if (!body || !page?.width) return null;

	const byName = new Map((elementDefs || []).filter(def => def?.name).map(def => [def.name, def]));
	// A record layout draws one sheet; a query layout draws one per selected
	// record, which is what makes a sheet of labels a sheet of labels.
	const records = body.subject === 'query' ? (items || []) : [item];
	const onForm = hasForm(body);
	const sheets = formSheets(body);

	// The rulers frame a sheet without being part of it, so the sheet's own box
	// (what drag arithmetic and the printer both read) is exactly as it was.
	const framed = (key, sheet, paper) => (interactive && rulers ? (
		<div key={key} className="page-sheet-frame">
			{/* The page's unit sits where the two rulers meet. */}
			<div className="page-ruler page-ruler--corner" aria-hidden="true">{page.unit}</div>
			<PageRuler axis="x" page={paper} />
			<PageRuler axis="y" page={paper} />
			{sheet}
		</div>
	) : sheet);

	// One sheet, or on a form one per page; the guides belong to the sheet the
	// carried block is on.
	const guidesHere = number => (interactive && guides && (!onForm || (guides.formPage || 1) === number) ? guides : null);

	return (
		<div className={`page-layout ${className}`.trim()}>
			{records.flatMap((record, index) => sheets.map(({ number, width, height }) => {
				const paper = sheetPage(body, number);
				const sheetKey = `${record?._id || `sheet-${index}`}${onForm ? `-page-${number}` : ''}`;
				const image = formImages?.[number];
				// The page is not in hand: no values on a blank sheet (see the header).
				const held = onForm && !interactive && image?.status !== 'ready';
				const lines = guidesHere(number);
				return framed(
					sheetKey,
					<div
						key={sheetKey}
						className={`page-sheet${interactive ? ' page-sheet--interactive' : ''}`}
						style={{
							width: unit(width, page.unit),
							height: unit(height, page.unit),
							// The margins are a guide, not a clip: a block is placed in
							// page coordinates and the author can put one in the margin
							// deliberately (a bleed, a corner mark).
							'--page-margin-top': unit(page.margins?.top, page.unit),
							'--page-margin-right': unit(page.margins?.right, page.unit),
							'--page-margin-bottom': unit(page.margins?.bottom, page.unit),
							'--page-margin-left': unit(page.margins?.left, page.unit),
						}}
						data-testid="page-sheet"
						{...(onForm ? { 'data-form-page': number } : {})}
					>
						{onForm ? (
							<PageFormBackdrop number={number} total={sheets.length} name={body.form.name} image={image} onRetry={onRetryForm} />
						) : null}
						{interactive ? <div className="page-sheet__guides" aria-hidden="true" /> : null}
						{interactive && gridStep ? (
							<div
								className="page-sheet__grid"
								data-testid="page-grid"
								aria-hidden="true"
								style={{ backgroundSize: `${gridDrawStep(page.unit, gridStep)}${page.unit} ${gridDrawStep(page.unit, gridStep)}${page.unit}` }}
							/>
						) : null}

						{lines ? [
							...(lines.x || []).map(at => (
								<div
									key={`guide-x-${at}`}
									className="page-sheet__alignment page-sheet__alignment--x"
									data-testid="page-guide-x"
									aria-hidden="true"
									style={{ left: unit(at, page.unit) }}
								/>
							)),
							...(lines.y || []).map(at => (
								<div
									key={`guide-y-${at}`}
									className="page-sheet__alignment page-sheet__alignment--y"
									data-testid="page-guide-y"
									aria-hidden="true"
									style={{ top: unit(at, page.unit) }}
								/>
							)),
						] : null}

						{(held ? [] : blocksOnSheet(body, number)).map(block => {
							// On a form the box is a limit, and what it cuts is told (KPR-1861).
							const fit = onForm ? fitName(block, record, byName, elementDefs, resolveLookupValue) : null;
							// Auto scale (KPR-1862): only a form's element or text block that has a value to fit.
							const scaled = Boolean(fit) && block.style?.autoScale === true && (block.type === 'element' || block.type === 'text');
							const group = block.type === 'group' ? readableGroup(byName.get(block.element)) : null;
							// The entries past the last line (the designer's sample has none).
							const notShown = group && !interactive ? notShownWords(entriesOnLines(record?.elements?.[block.element], block.lines)) : '';
							const inner = block.type === 'element' ? (
								<ElementBlock
									block={block}
									element={byName.get(block.element)}
									item={record}
									resolveLookupValue={resolveLookupValue}
									unitSystem={unitSystem}
								/>
							) : block.type === 'text' ? (
								<TextBlock
									block={block}
									item={record}
									elementDefs={elementDefs}
									resolveLookupValue={resolveLookupValue}
								/>
							) : block.type === 'badge' ? (
								<RecordBadge
									kind="item"
									record={card}
									kicker={card?.name}
									title={record?.displayValue || card?.name || ''}
									size="md"
								/>
							) : block.type === 'rule' ? (
								<div className="page-block__rule" style={{ borderTopWidth: `${block.style?.weight ? 2 : 1}px` }} />
							) : block.type === 'barcode' ? (
								<BarcodeBlock
									block={block}
									item={record}
									elementDefs={elementDefs}
									resolveLookupValue={resolveLookupValue}
									page={page}
								/>
							) : block.type === 'group' ? (
								<PageGroupBlock
									block={block}
									group={group}
									record={record}
									page={page}
									textStyle={blockTextStyle(block.style)}
									onForm={onForm}
									sample={interactive && Boolean(record)}
									resolveLookupValue={resolveLookupValue}
									unitSystem={unitSystem}
								/>
							) : block.type === 'query' ? (
								// Filled by the query-block renderer when one is given;
								// a sheet with no way to run a query simply leaves the
								// space it was allotted.
								(renderQueryBlock ? renderQueryBlock(block, record) : null)
							) : null;

							const node = (
								<div
									key={block.id}
									className={`page-block page-block--${block.type}${block.style?.muted ? ' page-block--muted' : ''}`}
									style={blockPosition(block, page)}
									data-block-id={block.id}
									{...(fit ? { 'data-fit-name': fit } : {})}
									{...(scaled ? { 'data-autoscale': '' } : {})}
									{...(notShown ? { 'data-not-shown': notShown, 'data-group-name': groupName(group) } : {})}
								>
									{inner}
								</div>
							);
							return renderBlock ? renderBlock(block, node) : node;
						})}
					</div>,
					paper,
				);
			}))}
		</div>
	);
}
